import { useRef, useState } from "react";
import type { ChatMessage } from "../../../types";
import { formatRelativeTime, isIOS, canPlayOnIOS, videoMime } from "../../../utils";
import { mediaUrl, refreshMediaToken } from "../../../mediaAuth";
import { logVideoPlaybackError } from "../../../videoDiagnostics";
import { VoiceMessagePlayer } from "./VoiceMessagePlayer";
import { PrivateDropChatCard } from "./PrivateDropChatCard";

function ReadReceiptIcon({ message }: { message: ChatMessage }) {
  if (message.failed) return <i className="bi bi-exclamation-circle-fill chat-receipt-failed" aria-hidden="true" />;
  if (message.pending) return <i className="bi bi-clock chat-receipt-pending" aria-hidden="true" />;
  if (message.readAt) return <i className="bi bi-check-all chat-receipt-read" aria-hidden="true" />;
  if (message.deliveredAt) return <i className="bi bi-check-all" aria-hidden="true" />;
  return <i className="bi bi-check" aria-hidden="true" />;
}

const MAX_IMG_AUTO_RETRIES = 2;
// Video playback audit, Bug D: same frozen-media-token failure mode as the
// image retry below, applied to video - a chat thread left open long
// enough for the media-auth token to expire (see mediaAuth.ts) previously
// just showed the permanent "can't play" fallback for what's really a
// stale token, indistinguishable from a genuine decode failure.
const MAX_VIDEO_AUTO_RETRIES = 2;

function Attachment({
  attachment,
  onOpenImage,
}: {
  attachment: NonNullable<ChatMessage["attachment"]>;
  onOpenImage: (src: string) => void;
}) {
  // Covers a case canPlayOnIOS's extension check can't: an iPhone-recorded
  // .mov is very often HEVC-encoded (Apple's default since iOS 11), which
  // Chrome/Firefox on non-Apple platforms generally can't decode at all -
  // regardless of container. There's no reliable way to know that from the
  // filename alone (unlike webm, which Safari never supports full stop),
  // so this reacts to the <video> element's own real playback failure
  // instead of guessing ahead of time - same fallback UI either way.
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoRetryKey, setVideoRetryKey] = useState(0);
  const videoRetryCountRef = useRef(0);
  // A chat thread can sit open/idle for a long time - the media auth token
  // baked into this <img>'s src at render time can go stale (tab
  // backgrounded, image cache evicted under memory pressure, PWA resume)
  // well before the browser actually needs to re-fetch it, since nothing
  // re-renders this bubble in between to pick up a refreshed token. A
  // couple of automatic retries (remounting with a freshly computed
  // mediaUrl(), which reflects whatever token is current *now*) self-heals
  // that instead of leaving a permanently broken image; a manual
  // tap-to-retry covers anything beyond that (e.g. genuinely offline).
  const [imgRetryKey, setImgRetryKey] = useState(0);
  const [imgFailed, setImgFailed] = useState(false);

  if (attachment.type === "audio") return <VoiceMessagePlayer attachment={attachment} />;
  if (attachment.type === "video") {
    if ((isIOS() && !canPlayOnIOS(attachment.filename)) || videoFailed) {
      return (
        <div className="chat-bubble-attachment">
          <a href={mediaUrl(`/api/chat/media/${attachment.filename}`)} target="_blank" rel="noopener">
            {videoFailed ? "Can't play this video here - open it instead" : "Open video"}
          </a>
        </div>
      );
    }
    return (
      <video
        key={videoRetryKey}
        className="chat-bubble-attachment"
        controls
        playsInline
        preload="metadata"
        onError={(e) => {
          logVideoPlaybackError("chat-bubble", e.currentTarget, attachment.filename);
          if (videoRetryCountRef.current < MAX_VIDEO_AUTO_RETRIES) {
            videoRetryCountRef.current += 1;
            refreshMediaToken().finally(() => setVideoRetryKey((k) => k + 1));
          } else {
            setVideoFailed(true);
          }
        }}
      >
        <source src={attachment.localBlobUrl ?? mediaUrl(`/api/chat/media/${attachment.filename}`)} type={videoMime(attachment.filename)} />
      </video>
    );
  }

  // A pending (not-yet-confirmed) attachment has no server thumbnail yet -
  // localBlobUrl (set by buildOptimisticMessage in ChatThreadScreen.tsx)
  // renders straight from the local file until the real message arrives.
  const fullSrc = attachment.localBlobUrl ?? mediaUrl(`/api/chat/media/${attachment.filename}`);
  const thumbSrc = attachment.localBlobUrl ?? (attachment.thumbFilename ? mediaUrl(`/api/chat/thumbs/${attachment.thumbFilename}`) : fullSrc);

  if (imgFailed) {
    return (
      <button
        type="button"
        className="chat-bubble-attachment chat-image-retry"
        onClick={() => {
          setImgFailed(false);
          setImgRetryKey((k) => k + 1);
        }}
      >
        <i className="bi bi-arrow-clockwise" aria-hidden="true" /> Couldn't load - tap to retry
      </button>
    );
  }

  return (
    <img
      key={imgRetryKey}
      className="chat-bubble-attachment"
      src={thumbSrc}
      alt="Photo"
      loading="lazy"
      onClick={() => onOpenImage(fullSrc)}
      onError={() => {
        if (imgRetryKey < MAX_IMG_AUTO_RETRIES) setImgRetryKey((k) => k + 1);
        else setImgFailed(true);
      }}
    />
  );
}

// Heart gets its own dedicated affordance (QuickHeartButton/corner badge
// below) instead of sitting in this row like every other reaction - this
// only ever shows the OTHER quick-reaction emojis (👍😂😮😢🙏, still
// reachable via long-press's full picker), so a message hearted alongside
// e.g. 👍 doesn't show the heart twice.
//
// Two rendering modes: a sent (own) message keeps this as a small floating
// badge overlapping the bubble's bottom-left corner (opposite side from
// the heart badge, so they never collide) - a received (other) message
// instead renders it as a plain inline row, since it now sits inside
// .chat-message-outside-meta alongside the time and heart button rather
// than inside the bubble at all (an absolutely-positioned badge there
// would overlap that row instead of joining it).
function Reactions({ reactions, inline }: { reactions: ChatMessage["reactions"]; inline?: boolean }) {
  const emojis = [...new Set(reactions.map((r) => r.emoji))].filter((e) => e !== "❤️");
  if (!emojis.length) return null;
  return (
    <div className={`chat-reactions${inline ? " is-inline" : ""}`}>
      {emojis.map((e) => (
        <span key={e} className="chat-reaction-badge">
          {e}
        </span>
      ))}
    </div>
  );
}

// The default, always-visible "like" affordance beside a message someone
// else sent - a faster path than the long-press popover for the single
// most common reaction, not a replacement for it (the popover's own ❤️
// option keeps working identically - both write to the exact same
// reactions array via the same reactToMessageApi call, so they can never
// disagree about whether a message is hearted). Floats OUTSIDE the bubble
// (there's room to its right since received bubbles are left-aligned) -
// own/sent bubbles hug the opposite edge instead, so they get a corner
// badge rather than an external button (see HeartBadge below).
function QuickHeartButton({ active, onToggle }: { active: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className={`chat-heart-btn${active ? " is-active" : ""}`}
      aria-label={active ? "Remove heart" : "Heart this message"}
      aria-pressed={active}
      onClick={onToggle}
    >
      <i className={`bi ${active ? "bi-heart-fill" : "bi-heart"}`} aria-hidden="true" />
    </button>
  );
}

// Read-only indicator on your OWN message that someone hearted it -
// there's no room to float an external button off a right-aligned bubble
// (that's the screen edge), so this overlaps the bubble's own corner
// instead, the same "badge" idiom GalleryCard.tsx's fav-heart-badge/
// gallery-new-badge already use for corner indicators elsewhere in this
// app. Not a toggle - you don't "like" your own sent message through this;
// it just reports that a heart reaction already exists on it (from
// whoever reacted, self-tapback via the long-press popover included).
function HeartBadge() {
  return (
    <span className="chat-heart-badge" aria-label="Hearted">
      <i className="bi bi-heart-fill" aria-hidden="true" />
    </span>
  );
}

export function MessageBubble({
  message,
  own,
  grouped,
  highlighted,
  currentUserEmail,
  onRetry,
  onOpenImage,
  onReact,
}: {
  message: ChatMessage;
  own: boolean;
  // True when the immediately preceding row is from the same side (see
  // ChatThreadScreen.tsx's renderMessages) - consecutive messages from one
  // person sit close together (a tight top margin); a sender switch gets a
  // larger gap so the conversation still reads as a back-and-forth, not one
  // undifferentiated column.
  grouped?: boolean;
  // Briefly flagged after Search Mode's jumpToMessage/jumpToDate scrolls to
  // this bubble (see ChatThreadScreen.tsx), so landing somewhere in the
  // middle of history is visually obvious, not just a silent scroll.
  highlighted?: boolean;
  currentUserEmail: string;
  onRetry?: (localId: string) => void;
  onOpenImage: (src: string) => void;
  onReact: (messageId: string, emoji: string | null) => void;
}) {
  const hasHeart = message.reactions.some((r) => r.emoji === "❤️");
  const rowClass = `chat-message-row ${own ? "is-own" : "is-other"}${grouped ? " is-grouped" : ""}`;

  if (message.deletedAt) {
    return (
      <div className={rowClass}>
        <div
          className={`chat-bubble ${own ? "is-own" : "is-other"} is-deleted${highlighted ? " is-highlighted" : ""}`}
          data-message-id={message.id}
        >
          <div className="chat-bubble-text muted fst-italic">Message deleted</div>
          {own && (
            <div className="chat-bubble-meta">
              <span className="chat-bubble-time">{formatRelativeTime(message.createdAt)}</span>
            </div>
          )}
        </div>
        {!own && <span className="chat-bubble-time-outside">{formatRelativeTime(message.createdAt)}</span>}
      </div>
    );
  }

  // A Private Drop card is a distinct visual, not a normal bubble with an
  // extra widget inside it - own message row for spacing, but bypasses the
  // whole .chat-bubble/reply/reactions/read-receipt chrome below entirely.
  if (message.card?.type === "private_drop") {
    return (
      <div className={rowClass}>
        <PrivateDropChatCard giftId={message.card.giftId} />
        {!own && <span className="chat-bubble-time-outside">{formatRelativeTime(message.createdAt)}</span>}
      </div>
    );
  }

  const statusClass = message.failed ? " is-failed" : message.pending ? " is-pending" : "";
  // Mine specifically (not "does this message have a heart from anyone") -
  // a like button reflects whether I've liked it, same as every reference
  // app this pattern is modeled on (Instagram/iMessage tapbacks are always
  // scoped to the viewer, not a shared on/off state).
  const myHeart = message.reactions.find((r) => r.userEmail === currentUserEmail)?.emoji === "❤️";

  return (
    <div className={rowClass}>
      <div
        className={`chat-bubble ${own ? "is-own" : "is-other"}${statusClass}${highlighted ? " is-highlighted" : ""}`}
        data-message-id={message.id}
        title="Hold for options"
      >
        {message.replyTo && (
          <div className="chat-reply-quote">
            <div className="chat-reply-quote-sender">{message.replyTo.senderName}</div>
            <div className="chat-reply-quote-snippet">{message.replyTo.snippet}</div>
          </div>
        )}
        {message.attachment && <Attachment attachment={message.attachment} onOpenImage={onOpenImage} />}
        {message.text && <div className="chat-bubble-text">{message.text}</div>}
        {/* Read receipts only ever apply to a message I sent (own), and the
            reference this redesign matches keeps a received bubble down to
            just its content - time/edited/heart all move to the outside
            meta row below instead of nesting inside here. */}
        {own && (
          <div className="chat-bubble-meta">
            {message.editedAt && (
              <span className="chat-bubble-edited" title={`Edited ${formatRelativeTime(message.editedAt)}`}>
                (edited)
              </span>
            )}
            <span className="chat-bubble-time">{formatRelativeTime(message.createdAt)}</span>
            <ReadReceiptIcon message={message} />
            {message.failed && message.localId && (
              <button type="button" className="chat-retry-btn" aria-label="Retry send" onClick={() => onRetry?.(message.localId!)}>
                <i className="bi bi-arrow-repeat" aria-hidden="true" /> Retry
              </button>
            )}
          </div>
        )}
        {own && <Reactions reactions={message.reactions} />}
        {own && hasHeart && <HeartBadge />}
      </div>
      {!own && (
        <div className="chat-message-outside-meta">
          {message.editedAt && (
            <span className="chat-bubble-edited" title={`Edited ${formatRelativeTime(message.editedAt)}`}>
              (edited)
            </span>
          )}
          <span className="chat-bubble-time-outside">{formatRelativeTime(message.createdAt)}</span>
          <Reactions reactions={message.reactions} inline />
          <QuickHeartButton active={myHeart} onToggle={() => onReact(message.id, myHeart ? null : "❤️")} />
        </div>
      )}
    </div>
  );
}
